import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, BellRing, MapPin } from "lucide-react";
import api from "@/lib/api";
import { Reveal, HeroLine, FadeIn } from "@/components/Reveal";
import { inputCls, Label, GoldButton } from "@/components/Field";
import { usePageTitle } from "@/hooks/use-page-title";

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-aspira-gold">{children}</p>
);

export const ListingCard = ({ listing, testid }) => (
  <div
    data-testid={testid}
    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-aspira-goldborder bg-white shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-aspira-gold hover:shadow-2xl"
  >
    {listing.image_url ? (
      <div className="overflow-hidden">
        <img
          src={listing.image_url}
          alt={`${listing.title} — vacant land in ${listing.county ? `${listing.county}, ` : ""}${listing.state}`}
          className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
    ) : (
      <div className="flex h-56 items-center justify-center bg-aspira-sand">
        <MapPin size={28} className="text-aspira-gold" />
      </div>
    )}
    <div className="flex flex-1 flex-col p-6">
      <h3 className="font-serif text-xl font-medium text-aspira-ink">{listing.title}</h3>
      <p className="mt-1 flex items-center gap-1.5 text-sm text-aspira-body">
        <MapPin size={13} className="text-aspira-gold" />
        {[listing.county, listing.state].filter(Boolean).join(", ")}
      </p>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-aspira-body">
        {listing.acreage && <span><span className="font-semibold text-aspira-ink">{listing.acreage}</span> acres</span>}
        {listing.price && <span className="font-serif text-lg text-aspira-gold">{listing.price}</span>}
      </div>
      {listing.description && (
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-aspira-body">{listing.description}</p>
      )}
      <Link
        to={`/buy/${listing.id}`}
        data-testid={`${testid}-view`}
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-aspira-ink/15 px-5 py-2.5 text-sm font-semibold text-aspira-ink transition-all duration-300 hover:border-aspira-gold hover:text-aspira-gold"
      >
        View Property <ArrowRight size={14} />
      </Link>
    </div>
  </div>
);

const parseNum = (v) => {
  const n = parseFloat(String(v || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const Filters = ({ listings, filters, setFilters }) => {
  const states = [...new Set(listings.map((l) => l.state).filter(Boolean))].sort();
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div data-testid="listing-filters" className="mt-8 grid gap-4 rounded-3xl border border-aspira-goldborder/60 bg-white p-5 sm:grid-cols-2 lg:grid-cols-4">
      <div>
        <Label htmlFor="filter-state">State</Label>
        <select id="filter-state" data-testid="filter-state" className={inputCls} value={filters.state} onChange={set("state")}>
          <option value="">All states</option>
          {states.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <Label htmlFor="filter-county">County</Label>
        <input id="filter-county" data-testid="filter-county" className={inputCls} placeholder="Any county" value={filters.county} onChange={set("county")} />
      </div>
      <div>
        <Label htmlFor="filter-acreage">Acreage</Label>
        <select id="filter-acreage" data-testid="filter-acreage" className={inputCls} value={filters.acreage} onChange={set("acreage")}>
          <option value="">Any size</option>
          <option value="0-1">Under 1 acre</option>
          <option value="1-5">1 – 5 acres</option>
          <option value="5-20">5 – 20 acres</option>
          <option value="20-99999">20+ acres</option>
        </select>
      </div>
      <div>
        <Label htmlFor="filter-price">Price</Label>
        <select id="filter-price" data-testid="filter-price" className={inputCls} value={filters.price} onChange={set("price")}>
          <option value="">Any price</option>
          <option value="0-10000">Under $10k</option>
          <option value="10000-50000">$10k – $50k</option>
          <option value="50000-150000">$50k – $150k</option>
          <option value="150000-99999999">$150k+</option>
        </select>
      </div>
    </div>
  );
};

const Inventory = () => {
  const [listings, setListings] = useState(null);
  const [filters, setFilters] = useState({ state: "", county: "", acreage: "", price: "" });

  useEffect(() => {
    api.get("/listings").then((r) => setListings(r.data.listings)).catch(() => setListings([]));
  }, []);

  const visible = useMemo(() => {
    if (!listings) return [];
    return listings.filter((l) => {
      if (filters.state && l.state !== filters.state) return false;
      if (filters.county && !(l.county || "").toLowerCase().includes(filters.county.toLowerCase())) return false;
      if (filters.acreage) {
        const [lo, hi] = filters.acreage.split("-").map(Number);
        const a = parseNum(l.acreage);
        if (a === null || a < lo || a >= hi) return false;
      }
      if (filters.price) {
        const [lo, hi] = filters.price.split("-").map(Number);
        const p = parseNum(l.price);
        if (p === null || p < lo || p >= hi) return false;
      }
      return true;
    });
  }, [listings, filters]);

  if (listings === null) {
    return <p className="mt-12 text-sm text-aspira-muted" data-testid="listings-loading">Loading properties…</p>;
  }

  if (listings.length === 0) {
    return (
      <Reveal>
        <div data-testid="listings-empty" className="mt-12 rounded-3xl border border-dashed border-aspira-goldborder bg-white/70 p-10 text-center sm:p-14">
          <p className="font-serif text-2xl font-medium text-aspira-ink">No properties are currently listed.</p>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-aspira-body">
            New land opportunities are added as they become available. Check back
            soon, or reach out and we'll keep an eye out for you.
          </p>
          <Link
            to="/contact"
            data-testid="listings-empty-contact"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-aspira-ink/15 px-6 py-3 text-sm font-semibold text-aspira-ink transition-all duration-300 hover:border-aspira-gold hover:text-aspira-gold"
          >
            Contact us about upcoming land <ArrowRight size={15} />
          </Link>
        </div>
      </Reveal>
    );
  }

  return (
    <>
      {listings.length >= 3 && (
        <Filters listings={listings} filters={filters} setFilters={setFilters} />
      )}
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="listings-grid">
        {visible.map((l, i) => (
          <Reveal key={l.id} delay={Math.min(i, 5) * 0.06}>
            <ListingCard listing={l} testid={`listing-card-${i + 1}`} />
          </Reveal>
        ))}
        {visible.length === 0 && (
          <p className="col-span-full rounded-2xl border border-aspira-goldborder/60 bg-white p-8 text-center text-sm text-aspira-body" data-testid="listings-no-match">
            No properties match those filters — try widening your search.
          </p>
        )}
      </div>
    </>
  );
};

const NotifyForm = () => {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", states: "", acreage_range: "", budget: "", use_case: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads/buyer", form);
      toast.success("You're on the list", {
        description: "We'll reach out when a property matches what you're looking for.",
      });
      setForm({ name: "", email: "", phone: "", states: "", acreage_range: "", budget: "", use_case: "" });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="buyer-notify-form"
      className="rounded-3xl border border-aspira-goldborder bg-white p-7 shadow-xl sm:p-9"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-aspira-goldlight text-aspira-gold">
          <BellRing size={18} />
        </span>
        <div>
          <h3 className="font-serif text-xl font-medium text-aspira-ink">Get notified about new land</h3>
          <p className="text-xs text-aspira-muted">Tell us what you're looking for and we'll reach out when there's a match.</p>
        </div>
      </div>
      <div className="mt-7 grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="buyer-name">Full name *</Label>
          <input id="buyer-name" data-testid="buyer-name" required className={inputCls} placeholder="Jane Doe" value={form.name} onChange={set("name")} />
        </div>
        <div>
          <Label htmlFor="buyer-email">Email *</Label>
          <input id="buyer-email" data-testid="buyer-email" required type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={set("email")} />
        </div>
        <div>
          <Label htmlFor="buyer-phone">Phone</Label>
          <input id="buyer-phone" data-testid="buyer-phone" className={inputCls} placeholder="(555) 123-4567" value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <Label htmlFor="buyer-states">States of interest</Label>
          <input id="buyer-states" data-testid="buyer-states" className={inputCls} placeholder="e.g. TX, AZ, NM" value={form.states} onChange={set("states")} />
        </div>
        <div>
          <Label htmlFor="buyer-acreage">Acreage range</Label>
          <select id="buyer-acreage" data-testid="buyer-acreage" className={inputCls} value={form.acreage_range} onChange={set("acreage_range")}>
            <option value="">Select one</option>
            <option>Under 1 acre</option>
            <option>1 – 5 acres</option>
            <option>5 – 20 acres</option>
            <option>20 – 100 acres</option>
            <option>100+ acres</option>
          </select>
        </div>
        <div>
          <Label htmlFor="buyer-budget">Budget</Label>
          <select id="buyer-budget" data-testid="buyer-budget" className={inputCls} value={form.budget} onChange={set("budget")}>
            <option value="">Select one</option>
            <option>Under $10k</option>
            <option>$10k – $25k</option>
            <option>$25k – $75k</option>
            <option>$75k – $150k</option>
            <option>$150k+</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="buyer-use">Intended use</Label>
          <select id="buyer-use" data-testid="buyer-use" className={inputCls} value={form.use_case} onChange={set("use_case")}>
            <option value="">Select one</option>
            <option>Recreation</option>
            <option>Future homesite</option>
            <option>Investment</option>
            <option>Other</option>
          </select>
        </div>
      </div>
      <GoldButton type="submit" data-testid="buyer-form-submit" disabled={sending} className="mt-8 w-full">
        {sending ? "Joining…" : "Notify Me"} <ArrowRight size={15} />
      </GoldButton>
      <p className="mt-4 text-center text-xs text-aspira-muted">
        Only relevant properties — never spam.
      </p>
    </form>
  );
};

const Buy = () => {
  usePageTitle("Land for Sale — Buy Vacant Land | Aspira Property Group");
  return (
    <>
      <section data-testid="buy-hero" className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-aspira-gold/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-5 pb-6 pt-16 sm:px-8 lg:pt-24">
          <FadeIn><Eyebrow>Buy land</Eyebrow></FadeIn>
          <h1 className="mt-6 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-aspira-ink sm:text-5xl">
            <HeroLine delay={0.15}>Find Land</HeroLine>
            <HeroLine delay={0.3}>
              With <em className="italic text-aspira-gold">Potential.</em>
            </HeroLine>
          </h1>
          <FadeIn delay={0.5}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-aspira-body">
              Explore available land opportunities from Aspira Property Group.
              Whether you're looking for recreational acreage, a future homesite,
              or your next investment, discover properties worth considering.
            </p>
          </FadeIn>
        </div>
      </section>

      <section data-testid="inventory-section" className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <Reveal>
          <Eyebrow>Available properties</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-aspira-ink sm:text-4xl">
            Land for sale
          </h2>
        </Reveal>
        <Inventory />
      </section>

      <section data-testid="notify-section" className="bg-aspira-sand py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Stay in the loop</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-aspira-ink sm:text-4xl">
              Looking for something specific?
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-aspira-body">
              Land moves quickly. Tell us the states, acreage, and budget you have
              in mind, and we'll contact you directly when a property fits — before
              it's widely advertised.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <NotifyForm />
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Buy;
